import React, { useState } from "react";
import SidbarComponent from "./SidbarComponent";
import TopBarComponent from "./TopBarComponent";
import { FooterComponents } from "./FooterComponent";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  Search,
  Users,
  Loader2,
  CheckCircle,
  XCircle,
  GraduationCap,
  RefreshCw,
  Building2,
  Eye,
  ArrowLeft,
  ClipboardList,
  DollarSign,
} from "lucide-react";
import { getToken } from "../utils/auth";

const formatCurrency = (amount) =>
  amount != null
    ? new Intl.NumberFormat("en-RW", { style: "currency", currency: "RWF", maximumFractionDigits: 0 }).format(amount)
    : "—";

const statusColor = (status) => {
  switch (status) {
    case "APPROVED":
      return "bg-green-100 text-green-800 border-green-200";
    case "VERIFIED":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "RETURNED":
      return "bg-red-100 text-red-800 border-red-200";
    default:
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
  }
};

export default function HliAdminLoanView() {
  // Two views: "settings" (default overview) and "loans" (drill-down)
  const [view, setView] = useState("settings");
  const [selectedSetting, setSelectedSetting] = useState(null);

  // Loans view state
  const [showGrantedOnly, setShowGrantedOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0); // 0-indexed (Spring convention)
  const [updatingId, setUpdatingId] = useState(null);
  const PAGE_SIZE = 100;

  // ── Settings overview ────────────────────────────────────────────────────────
  const {
    data: settings = [],
    isLoading: settingsLoading,
    isError: settingsError,
    refetch: refetchSettings,
  } = useQuery({
    queryKey: ["hliMySettings"],
    queryFn: async () => {
      const res = await axios.get(
        process.env.REACT_APP_BASE_URL + "hec/settings/getSettingsByMyInstitution",
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      return res.data;
    },
    onError: (err) => {
      const msg = err.response?.data || "Failed to load settings.";
      toast.error(typeof msg === "string" ? msg : "Failed to load settings.", {
        position: "top-center",
        autoClose: 5000,
        theme: "colored",
      });
    },
  });

  // ── Loans drill-down ─────────────────────────────────────────────────────────
  const loansEndpoint = showGrantedOnly
    ? "hec/localLoan/getGrantedLocalLoansByMyInstitution"
    : "hec/localLoan/getLocalLoansByMyInstitution";

  const {
    data: loansPage = null,
    isLoading: loansLoading,
    isError: loansError,
    refetch: refetchLoans,
  } = useQuery({
    queryKey: ["hliLoans", selectedSetting?.id, showGrantedOnly, currentPage],
    queryFn: async () => {
      if (!selectedSetting) return null;
      const res = await axios.get(
        process.env.REACT_APP_BASE_URL + loansEndpoint,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
          params: {
            studyAreaId: selectedSetting.studentCategory,
            academicYearId: selectedSetting.academicYear?.academicYearName,
            page: currentPage,
            size: PAGE_SIZE,
          },
        }
      );
      return res.data; // Spring Page object: { content, totalElements, totalPages, number }
    },
    enabled: view === "loans" && !!selectedSetting,
    onError: (err) => {
      const msg = err.response?.data || "Failed to fetch loans.";
      toast.error(typeof msg === "string" ? msg : "Failed to fetch loans.", {
        position: "top-center",
        autoClose: 5000,
        theme: "colored",
      });
    },
  });

  const loans = loansPage?.content ?? [];

  const updateLoanStatus = async (loanId, status) => {
    setUpdatingId(loanId);
    try {
      await axios.put(
        process.env.REACT_APP_BASE_URL + "hec/localLoan/updateLoanStatusByMyInstitution",
        null,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
          params: { loanId, status },
        }
      );
      toast.success(`Loan ${status === "GRANTED" ? "approved" : "rejected"} successfully.`, {
        position: "top-center",
        autoClose: 4000,
        theme: "colored",
      });
      refetchLoans();
    } catch (err) {
      const msg = err.response?.data || "Failed to update loan status.";
      toast.error(typeof msg === "string" ? msg : "Failed to update loan status.", {
        position: "top-center",
        autoClose: 5000,
        theme: "colored",
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const openLoansView = (setting) => {
    setSelectedSetting(setting);
    setShowGrantedOnly(false);
    setSearchTerm("");
    setCurrentPage(0);
    setView("loans");
  };

  const backToSettings = () => {
    setView("settings");
    setSelectedSetting(null);
    setSearchTerm("");
    setCurrentPage(0);
  };

  const handleTabChange = (grantedOnly) => {
    setShowGrantedOnly(grantedOnly);
    setCurrentPage(0);
    setSearchTerm("");
  };

  const filtered = loans.filter(
    (l) =>
      l.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      l.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      l.regNumber?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const serverTotalPages = loansPage?.totalPages ?? 0;
  const serverTotalElements = loansPage?.totalElements ?? 0;

  React.useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm]);

  const getLoanStatusBadge = (status) => {
    if (status === "GRANTED" || status === "APPROVED") {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
          <CheckCircle className="w-3 h-3" />
          {status}
        </span>
      );
    } else if (status === "REJECTED") {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 border border-red-200">
          <XCircle className="w-3 h-3" />
          Rejected
        </span>
      );
    }
    return (
      <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
        {status || "Pending"}
      </span>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <TopBarComponent />
      <div className="flex flex-1">
        <SidbarComponent />

        <main className="flex-1 p-6 lg:p-8 overflow-auto">

          {/* ── SETTINGS OVERVIEW VIEW ─────────────────────────────────────── */}
          {view === "settings" && (
            <>
              {/* Header */}
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <Building2 className="w-7 h-7 text-cyan-600" />
                    My Institution — Selection Settings
                  </h1>
                  <p className="mt-1 text-sm text-gray-500">
                    Overview of loan selection settings for your institution's study categories.
                  </p>
                </div>
                <button
                  onClick={() => refetchSettings()}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-cyan-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>

              {/* Settings Table */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                {settingsLoading ? (
                  <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                    <Loader2 className="w-10 h-10 animate-spin text-cyan-500 mb-3" />
                    <p className="text-sm">Loading settings...</p>
                  </div>
                ) : settingsError ? (
                  <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                    <p className="text-sm font-medium text-red-500">Failed to load settings.</p>
                    <button
                      onClick={() => refetchSettings()}
                      className="mt-3 text-sm text-cyan-600 hover:underline"
                    >
                      Try again
                    </button>
                  </div>
                ) : settings.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                    <ClipboardList className="w-10 h-10 mb-3" />
                    <p className="text-sm font-medium">No selection settings found for your institution</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">#</th>
                          <th className="px-4 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Category</th>
                          <th className="px-4 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">Students Quota</th>
                          <th className="px-4 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Living Allowance</th>
                          <th className="px-4 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Total Living</th>
                          <th className="px-4 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Tuition Fee</th>
                          <th className="px-4 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Total Tuition</th>
                          <th className="px-4 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Academic Year</th>
                          <th className="px-4 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                          <th className="px-4 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {settings.map((s, i) => (
                          <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-4 text-gray-400 text-xs font-mono">{i + 1}</td>
                            <td className="px-4 py-4">
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-cyan-50 border border-cyan-200 text-cyan-800 text-xs font-semibold rounded-full">
                                <GraduationCap className="w-3.5 h-3.5" />
                                {s.studentCategory}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-center font-semibold text-gray-700">{s.selectedNumber ?? "—"}</td>
                            <td className="px-4 py-4 text-right text-gray-600">{formatCurrency(s.livingAllowancePerStudent)}</td>
                            <td className="px-4 py-4 text-right font-medium text-gray-700">{formatCurrency(s.totalLivingAllowance)}</td>
                            <td className="px-4 py-4 text-right text-gray-600">{formatCurrency(s.tuitionFeePerStudent)}</td>
                            <td className="px-4 py-4 text-right font-medium text-gray-700">{formatCurrency(s.totalTuitionFees)}</td>
                            <td className="px-4 py-4 text-gray-600">{s.academicYear?.academicYearName ?? "—"}</td>
                            <td className="px-4 py-4">
                              <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${statusColor(s.status)}`}>
                                {s.status ?? "—"}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <button
                                onClick={() => openLoansView(s)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                View Students
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── LOANS DRILL-DOWN VIEW ─────────────────────────────────────── */}
          {view === "loans" && selectedSetting && (
            <>
              {/* Header */}
              <div className="mb-6">
                <button
                  onClick={backToSettings}
                  className="inline-flex items-center gap-1.5 text-sm text-cyan-600 hover:text-cyan-800 font-medium mb-4"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Settings Overview
                </button>

                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                      <Users className="w-7 h-7 text-cyan-600" />
                      Student Loan Applications
                    </h1>
                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-cyan-50 border border-cyan-200 text-cyan-800 text-xs font-semibold rounded-full">
                        <GraduationCap className="w-3.5 h-3.5" />
                        {selectedSetting.studentCategory}
                      </span>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 border border-gray-200 text-gray-700 text-xs font-semibold rounded-full">
                        {selectedSetting.academicYear?.academicYearName}
                      </span>
                      <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${statusColor(selectedSetting.status)}`}>
                        {selectedSetting.status}
                      </span>
                    </div>
                  </div>

                  {/* Summary mini-cards */}
                  <div className="hidden sm:flex gap-3">
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 text-right min-w-[120px]">
                      <p className="text-xs text-gray-500 flex items-center justify-end gap-1 mb-1">
                        <Users className="w-3.5 h-3.5" /> Quota
                      </p>
                      <p className="text-lg font-bold text-cyan-700">{selectedSetting.selectedNumber ?? "—"}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 text-right min-w-[140px]">
                      <p className="text-xs text-gray-500 flex items-center justify-end gap-1 mb-1">
                        <DollarSign className="w-3.5 h-3.5" /> Loaded
                      </p>
                      <p className="text-lg font-bold text-gray-800">{serverTotalElements}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-2 mb-5">
                <button
                  onClick={() => handleTabChange(false)}
                  className={`px-5 py-2 text-sm font-semibold rounded-lg border transition-colors ${
                    !showGrantedOnly
                      ? "bg-cyan-600 text-white border-cyan-600 shadow"
                      : "bg-white text-gray-600 border-gray-300 hover:border-cyan-400 hover:text-cyan-700"
                  }`}
                >
                  All Applications
                </button>
                <button
                  onClick={() => handleTabChange(true)}
                  className={`px-5 py-2 text-sm font-semibold rounded-lg border transition-colors ${
                    showGrantedOnly
                      ? "bg-green-600 text-white border-green-600 shadow"
                      : "bg-white text-gray-600 border-gray-300 hover:border-green-400 hover:text-green-700"
                  }`}
                >
                  Granted Only
                </button>
                <button
                  onClick={() => refetchLoans()}
                  className="ml-auto flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-cyan-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>

              {/* Search */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-5">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name or registration number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                  />
                </div>
              </div>

              {/* Loans Table */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                {loansLoading ? (
                  <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                    <Loader2 className="w-10 h-10 animate-spin text-cyan-500 mb-3" />
                    <p className="text-sm">Loading applications...</p>
                  </div>
                ) : loansError ? (
                  <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                    <p className="text-sm font-medium text-red-500">Failed to load applications.</p>
                    <button
                      onClick={() => refetchLoans()}
                      className="mt-3 text-sm text-cyan-600 hover:underline"
                    >
                      Try again
                    </button>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                    <Users className="w-10 h-10 mb-3" />
                    <p className="text-sm font-medium">No applications found</p>
                    {searchTerm && <p className="text-xs mt-1">Try adjusting your search</p>}
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">#</th>
                            <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                            <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Reg. Number</th>
                            <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Gender</th>
                            <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Study Area</th>
                            <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Score</th>
                            <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                            {!showGrantedOnly && (
                              <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {filtered.map((loan, index) => (
                            <tr key={loan.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-5 py-4 text-gray-400 text-xs font-mono">{currentPage * PAGE_SIZE + index + 1}</td>
                              <td className="px-5 py-4 font-semibold text-gray-900">
                                {loan.firstName} {loan.lastName}
                              </td>
                              <td className="px-5 py-4 font-mono text-gray-700">{loan.regNumber || "—"}</td>
                              <td className="px-5 py-4 text-gray-600">{loan.gender || "—"}</td>
                              <td className="px-5 py-4 text-gray-600">{loan.studyAreaId || "—"}</td>
                              <td className="px-5 py-4">
                                {loan.totalScore != null ? (
                                  <span
                                    className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg border ${
                                      loan.totalScore >= 80
                                        ? "bg-green-100 text-green-800 border-green-200"
                                        : loan.totalScore >= 60
                                        ? "bg-yellow-100 text-yellow-800 border-yellow-200"
                                        : "bg-red-100 text-red-800 border-red-200"
                                    }`}
                                  >
                                    {loan.totalScore}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                              <td className="px-5 py-4">{getLoanStatusBadge(loan.status)}</td>
                              {!showGrantedOnly && (
                                <td className="px-5 py-4">
                                  {loan.status !== "GRANTED" && loan.status !== "REJECTED" ? (
                                    <div className="flex items-center gap-2">
                                      <button
                                        disabled={updatingId === loan.id}
                                        onClick={() => updateLoanStatus(loan.id, "GRANTED")}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                                      >
                                        {updatingId === loan.id ? (
                                          <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                          <CheckCircle className="w-3 h-3" />
                                        )}
                                        Approve
                                      </button>
                                      <button
                                        disabled={updatingId === loan.id}
                                        onClick={() => updateLoanStatus(loan.id, "REJECTED")}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                                      >
                                        {updatingId === loan.id ? (
                                          <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                          <XCircle className="w-3 h-3" />
                                        )}
                                        Reject
                                      </button>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-gray-400">—</span>
                                  )}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {serverTotalPages > 1 && (
                      <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 bg-gray-50">
                        <p className="text-xs text-gray-500">
                          Page {currentPage + 1} of {serverTotalPages} &mdash; {serverTotalElements} total
                        </p>
                        <div className="flex items-center gap-2">
                          <button
                            disabled={currentPage === 0}
                            onClick={() => setCurrentPage((p) => p - 1)}
                            className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-white transition-colors"
                          >
                            Previous
                          </button>
                          <span className="text-xs text-gray-600">{currentPage + 1} / {serverTotalPages}</span>
                          <button
                            disabled={currentPage + 1 >= serverTotalPages}
                            onClick={() => setCurrentPage((p) => p + 1)}
                            className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-white transition-colors"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </main>
      </div>
      <FooterComponents />
    </div>
  );
}
